<script>
import { app } from '@/main'
import { mapActions } from 'vuex'
import { required, maxLength as textMaxLength, requiredIf } from 'vuelidate/lib/validators'
import { withParams } from 'vuelidate/lib/validators/common'
import { formatSlug } from '@/support/utils/stringHelper'
import { getRichTextContentLength } from '@/support/utils/richTextHelper'
import { solutionTypeFormatter } from '@/support/utils/solutionTypeFormatter'
import wizardForm from '@/mixins/wizardForm'

const TRANSLATION = {
  description: {
    description: app.$t('global:form.thematicPages.description')
  }
}

const RICH_TEXT_EDITOR_TOOLBAR = [
  [
    'bold',
    'italic',
    'underline',
    'strike'
  ],
  [ 'clean' ],
  [ 'link' ]
]

const richTextMaxLength = (max) => withParams({
  type: 'maxLength',
  max
}, (value) => {
  if (!value) return true

  return getRichTextContentLength(value) <= max
})

const isValidURL = function (event) {
  if (!event) return true
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' +
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +
      '((\\d{1,3}\\.){3}\\d{1,3}))' +
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' +
      '(\\?[;&a-z\\d%_.~+=-]*)?' +
      '(\\#[-a-z\\d_]*)?$',
    'i'
  )

  return !!pattern.test(event)
}

export default {
  name: 'ThematicPagesManage',
  components: {
    Tabs: () => import('@/components/general/Tabs'),
    ModalConfirm: () => import('@/components/macro/ModalConfirm'),
    Radio: () => import('@/components/general/Radio'),
    Upload: () => import('@/components/general/Upload'),
    Action: () => import('@/components/general/Action'),
    Divisor: () => import('@/components/general/Divisor'),
    Datatable: () => import('@/components/general/Datatable'),
    ActionBar: () => import('@/components/general/ActionBar'),
    InputField: () => import('@/components/general/InputField'),
    ThematicPagesColors: () => import('@/modules/ThematicPages/components/ThematicPagesColors.vue'),
    Pagination: () => import('@/components/general/Pagination'),
    FormSection: () => import('@/components/general/FormSection'),
    SelectField: () => import('@/components/general/SelectField'),
    ContentHeader: () => import('@/components/general/ContentHeader'),
    FilterListSearch: () => import('@/components/general/FilterListSearch'),
    RichTextEditor: () => import('@/components/general/RichTextEditor'),
    Icon: () => import('@/components/general/Icon')
  },

  mixins: [ wizardForm ],

  data () {
    return {
      activeTab: 1,
      selectionTabActive: 0,
      orderingTabActive: 0,

      yesNoOptions: [
        {
          label: this.$t('global:yes'),
          value: true
        },
        {
          label: this.$t('global:no'),
          value: false
        }
      ],

      paths: [],
      solutions: [],
      programs: [],
      tools: [],
      showSolutions: false,
      showPaths: false,
      showPrograms: false,
      showTools: false,
      showFeaturedContents: false,
      thematicPageTypesList: [],

      sortingTypeContents: 'default',
      sortingTypeFeatured: 'default',

      formData: {
        id: null,
        thematicPageTypeId: null,
        thematicPageAlias: null,
        name: null,
        subtitle: null,
        callText: null,
        description: null,
        imageBanner: null,
        imageLogo: null,
        favicon: null,
        colorHeader: '',
        slug: null,
        sortingTypeSolution: 'default',
        sortingTypeProgram: 'default',
        sortingTypeLearningPath: 'default',
        sortingTypeTool: 'default',
        colorBottom: '',
        colorMenu: '',
        showList: false,
        showOnVitrine: false,
        seoTitle: null,
        seoDescription: null,
        seoKeywords: null,
        thematicPageSolutions: [],
        thematicPageLearningPaths: [],
        thematicPagePrograms: [],
        thematicPageTools: [],
        thematicPageContents: [],
        facebook: null,
        instagram: null,
        linkedin: null,
        twitter: null,
        accessCode: null,
        restrictedAccess: false,
        restrictedAccessTitle: this.$t('global:form.thematicPages.accesscode.restricted.access.title.text.default'),
        restrictedAccessText: this.$t('global:form.thematicPages.accesscode.restricted.access.text.default'),
        privacyNotice: false,
        privacyNoticeDescription: this.$t('global:form.thematicPages.privacyNotice.text.default'),
        featuredContents: [],
        isCreciSp: false,
        partnerId: null,
        isRepasse: false
      },

      partnersListOptions: [],

      backUrl: { name: 'thematicPages.index' },
      modalActiveThematicPageOpen: false,
      isSlugDuplicate: false,
      formatedUrl: '',
      isLoadingMoreSolutions: false,
      isLoadingMorePaths: false,
      isLoadingMorePrograms: false,
      isLoadingMoreTools: false,

      solutionsPagination: {
        query: {
          name: null,
          limit: 300
        },

        filter: {
          active: 1
        },

        order: {
          name: 'ASC'
        },

        lastPage: null,
        limit: 6,
        page: 1
      },

      pathsPagination: {
        query: {
          name: null,
          limit: 300
        },

        filter: {
          active: 1
        },

        order: {
          name: 'ASC'
        },

        lastPage: null,
        limit: 6,
        page: 1
      },

      programsPagination: {
        query: {
          name: null,
          limit: 300
        },

        filter: {
          active: 1
        },

        order: {
          name: 'ASC'
        },

        lastPage: null,
        limit: 6,
        page: 1
      },

      toolsPagination: {
        query: {
          title: null,
          limit: 300
        },

        filter: {
          active: 1,
          tool_type_alias: 'diagnostic',
          category: 'Ferramenta Sebrae'
        },

        order: {
          title: 'ASC'
        },

        lastPage: null,
        limit: 6,
        page: 1
      },

      solutionInfiniteScrollId: new Date().getTime(),

      pathsInfiniteScrollId: new Date().getTime(),

      programsInfiniteScrollId: new Date().getTime(),

      toolsInfiniteScrollId: new Date().getTime(),

      maxSteps: 4
    }
  },

  validations () {
    return {
      formData: {
        thematicPageTypeId: { required },
        partnerId: {
          required: requiredIf(function () {
            return this.formData.thematicPageAlias === 'corporative'
          })
        },

        name: { required },
        slug: {
          required,
          regexSlug: (event) => {
            const regexSlug = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

            return regexSlug.test(event)
          },

          slugDuplicate: () => {
            return this.isSlugDuplicate
          }
        },

        subtitle: { required },
        callText: { required },
        description: {
          required,
          maxLength: (value) => {
            const type = this.formData.thematicPageAlias
            const max = type === 'corporative' ? 600 : 2500

            return richTextMaxLength(max)(value)
          }
        },

        imageBanner: { required },
        imageLogo: { required },

        showList: {
          required: (value) => {
            return value !== undefined && value !== null
          }
        },

        facebook: {
          maxLength: textMaxLength(250),
          validURL: isValidURL
        },

        instagram: {
          maxLength: textMaxLength(250),
          validURL: isValidURL
        },

        twitter: {
          maxLength: textMaxLength(250),
          validURL: isValidURL
        },

        linkedin: {
          maxLength: textMaxLength(250),
          validURL: isValidURL
        },

        restrictedAccess: {
          required: (event) => {
            return event !== null && event !== undefined
          }
        },

        restrictedAccessText: {
          required: requiredIf(function () {
            return this.formData.restrictedAccess
          })
        },

        restrictedAccessTitle: {
          required: requiredIf(function () {
            return this.formData.restrictedAccess
          })
        },

        accessCode: {
          required: requiredIf(function () {
            return this.formData.restrictedAccess
          })
        },

        privacyNotice: {
          required: (event) => {
            return event !== null && event !== undefined
          }
        },

        privacyNoticeDescription: {
          required: requiredIf(function () {
            return this.formData.privacyNotice
          })
        }
      }
    }
  },

  computed: {
    isCorporativePage () {
      return this.formData.thematicPageAlias === 'corporative'
    },

    computedSelectionTabs () {
      const tabs = [
        { text: this.$t('thematicPages.manage:link.tab.1') },
        { text: this.$t('thematicPages.manage:link.tab.2') },
        { text: this.$t('thematicPages.manage:link.tab.3') },
        { text: this.$t('thematicPages.manage:link.tab.4') }
      ]

      if (this.isCorporativePage) {
        tabs.push({ text: this.$t('thematicPages.manage:link.tab.5') })
      }

      return tabs
    },

    computedOrderingTabs () {
      const tabs = [{ text: this.$t('thematicPages.manage:ordering.tab.1') }]

      if (this.isCorporativePage) {
        tabs.push({ text: this.$t('thematicPages.manage:ordering.tab.2') })
      }

      return tabs
    },

    computedDefaultManualOptions () {
      return [
        {
          label: this.$t('global:default'),
          value: 'default'
        },
        {
          label: this.$t('global:manual'),
          value: 'manual'
        }
      ]
    },

    availableSolutions () {
      return this.solutions
        .filter(
          (solution) => !this.formData.thematicPageSolutions.find((findSolution) => solution.id === findSolution.id)
        )
        .sort((a, b) => {
          if (a > b) return 1
          if (b > a) return -1

          return 0
        })
    },

    availablePaths () {
      return this.paths
        .filter((path) => !this.formData.thematicPageLearningPaths.find((findPath) => path.id === findPath.id))
        .sort((a, b) => {
          if (a > b) return 1
          if (b > a) return -1

          return 0
        })
    },

    availablePrograms () {
      return this.programs
        .filter((program) => !this.formData.thematicPagePrograms.find((findProgram) => program.id === findProgram.id))
        .sort((a, b) => {
          if (a > b) return 1
          if (b > a) return -1

          return 0
        })
    },

    availableTools () {
      return this.tools
        .filter((tool) => !this.formData.thematicPageTools.find((findTool) => tool.id === findTool.id))
        .sort((a, b) => {
          if (a.title > b.title) return 1
          if (b.title > a.title) return -1

          return 0
        })
    },

    isEditing () {
      return this.$route.params.id || this.formData.id
    },

    hasSelectedContents () {
      return (
        (this.showSolutions && this.formData.thematicPageSolutions.length > 0) ||
        (this.showPaths && this.formData.thematicPageLearningPaths.length > 0) ||
        (this.showPrograms && this.formData.thematicPagePrograms.length > 0) ||
        (this.showTools && this.formData.thematicPageTools.length > 0)
      )
    },

    linkedContents () {
      const contents = []

      if (this.showSolutions && this.formData.thematicPageSolutions) {
        this.formData.thematicPageSolutions.forEach(solution => {
          contents.push({
            id: solution.id,
            name: solution.name,
            type: 'solution',
            contentType: 'solution',
            solutionType: 'solution'
          })
        })
      }

      if (this.showPaths && this.formData.thematicPageLearningPaths) {
        this.formData.thematicPageLearningPaths.forEach(path => {
          contents.push({
            id: path.id,
            name: path.name,
            type: 'learning_path',
            contentType: 'learning_path',
            solutionType: 'learning_path'
          })
        })
      }

      if (this.showPrograms && this.formData.thematicPagePrograms) {
        this.formData.thematicPagePrograms.forEach(program => {
          contents.push({
            id: program.id,
            name: program.name,
            type: 'program',
            contentType: 'program',
            solutionType: 'program'
          })
        })
      }

      if (this.showTools && this.formData.thematicPageTools) {
        this.formData.thematicPageTools.forEach(tool => {
          contents.push({
            id: tool.id,
            name: tool.title,
            type: 'tool',
            contentType: 'tool',
            solutionType: 'tool'
          })
        })
      }

      return contents
    },

    availableFeaturedContents () {
      return this.linkedContents.filter(content =>
        !this.formData.featuredContents.find(featured =>
          featured.id === content.id && featured.contentType === content.contentType
        )
      )
    },

    maxStepsCount () {
      return 4
    }

  },

  watch: {
    'formData.thematicPageContents': {
      handler (newVal) {
        if (this.sortingTypeContents === 'manual' && newVal && newVal.length > 0) {
          const needsUpdate = newVal.some((item, index) => item.sorting !== index + 1)

          if (needsUpdate) {
            this.updateUnifiedContentsSorting()
          }
        }
      }
    },

    'formData.featuredContents': {
      handler (newVal) {
        if (this.sortingTypeFeatured === 'manual' && newVal && newVal.length > 0) {
          const needsUpdate = newVal.some((item, index) => item.sorting !== index + 1)

          if (needsUpdate) {
            this.updateFeaturedContentsSorting()
          }
        }
      }
    },

    'formData.thematicPageTypeId' () {
      const selectedThematicPageAlias = this.thematicPageTypesList.filter((type) => type.value === this.formData.thematicPageTypeId)

      if (!selectedThematicPageAlias[0]) return

      this.formData.thematicPageAlias = (selectedThematicPageAlias[0].alias || '').toLowerCase().trim()

      this.updateFormattedUrl()

      if (this.formData.thematicPageAlias !== 'corporative') {
        this.formData.colorMenu = ''
        this.formData.featuredContents = []
        this.showFeaturedContents = false
        this.formData.partnerId = null

        if (this.selectionTabActive === 4) {
          this.selectionTabActive = 0
        }

        if (this.orderingTabActive === 1) {
          this.orderingTabActive = 0
        }
      }
    },

    'formData.name' () {
      if (!this.$v.formData.slug.$dirty && !this.isEditing) {
        this.formData.slug = formatSlug(this.formData.name)
      }
    },

    'formData.slug' (event) {
      this.formData.slug = event.replace(/-{2,}/g, '-')

      this.debounceEvent(async () => {
        this.setFetching(true)
        const search = { slug: event }

        if (this.isEditing) {
          search.not_id = [ this.formData.id ]
        }

        const thematicPages = await this.attemptGetThematicPagesList({ filter: search }).then((pagination) => {
          return pagination.data
        })

        this.isSlugDuplicate = !(thematicPages.length > 0)

        if (!this.isSlugDuplicate) {
          this.$v.formData.slug.$touch()
        }

        this.setFetching(false)
      }, 1000)

      this.updateFormattedUrl()
    },

    'formData.facebook' (value) {
      if (value && !value.startsWith('https://')) {
        this.formData.facebook = `https://${this.formData.facebook}`
      }
    },

    'formData.instagram' (value) {
      if (value && !value.startsWith('https://')) {
        this.formData.instagram = `https://${this.formData.instagram}`
      }
    },

    'formData.twitter' (value) {
      if (value && !value.startsWith('https://')) {
        this.formData.twitter = `https://${this.formData.twitter}`
      }
    },

    'formData.linkedin' (value) {
      if (value && !value.startsWith('https://')) {
        this.formData.linkedin = `https://${this.formData.linkedin}`
      }
    },

    // A lista de ordenacao e uma copia mantida a mao, e ate aqui so era
    // reconstruida no submit — por isso continuava exibindo conteudos ja
    // desvinculados ate salvar. syncUnifiedContents le tanto as colecoes
    // quanto os toggles, entao precisa ser disparado pelas duas fontes.
    'formData.thematicPageSolutions' () {
      this.syncUnifiedContents()
      this.cleanupFeaturedContents()
    },

    'formData.thematicPageLearningPaths' () {
      this.syncUnifiedContents()
      this.cleanupFeaturedContents()
    },

    'formData.thematicPagePrograms' () {
      this.syncUnifiedContents()
      this.cleanupFeaturedContents()
    },

    'formData.thematicPageTools' () {
      this.syncUnifiedContents()
      this.cleanupFeaturedContents()
    },

    showSolutions () {
      this.syncUnifiedContents()
      this.cleanupFeaturedContents()
    },

    showPaths () {
      this.syncUnifiedContents()
      this.cleanupFeaturedContents()
    },

    showPrograms () {
      this.syncUnifiedContents()
      this.cleanupFeaturedContents()
    },

    showTools () {
      this.syncUnifiedContents()
      this.cleanupFeaturedContents()
    }
  },

  created () {
    this.setup().then(() => {
      if (this.$route.params.id) {
        this.setFetching(true)

        this.attemptGetThematicPage(this.$route.params.id)
          .then(({ data }) => {
            this.formData = {
              ...this.formData,
              ...data
            }

            this.formData.thematicPageTypeId = data.thematicPageType.id
            this.formData.thematicPageAlias = (data.thematicPageType.alias || '').toLowerCase().trim()
            this.formData.colorHeader = data.colorHeader || ''
            this.formData.partnerId = data.partner?.id || null
            this.formData.isRepasse = !!data.isRepasse

            this.formData.showOnVitrine = Boolean(data.showOnVitrine ?? false)

            this.formData.restrictedAccess = !!this.formData.restrictedAccess

            this.formData.restrictedAccessText = this.formData.restrictedAccessText?.length
              ? this.formData.restrictedAccessText
              : this.$t('global:form.thematicPages.accesscode.restricted.access.text.default')

            this.formData.restrictedAccessTitle = this.formData.restrictedAccessTitle?.length
              ? this.formData.restrictedAccessTitle
              : this.$t('global:form.thematicPages.accesscode.restricted.access.title.text.default')

            this.processThematicPageContents(data)
            this.processFeaturedContents(data)

            if (data.sortingTypeSolution !== undefined && data.sortingTypeSolution !== null) {
              this.sortingTypeContents = (data.sortingTypeSolution === true || data.sortingTypeSolution === 'manual') ? 'manual' : 'default'
            }

            if (data.sortingTypeFeatured !== undefined && data.sortingTypeFeatured !== null) {
              this.sortingTypeFeatured = (data.sortingTypeFeatured === true || data.sortingTypeFeatured === 'manual') ? 'manual' : 'default'
            }

            this.$nextTick(() => {
              this.snapshotWizardState()
            })
          })
          .catch(() => {
            this.setFeedback({ message: this.$t('thematicPages.load:fetch.error') })
            this.$router.push({ name: 'thematicPages.index' })
          })
          .finally(() => {
            this.setFetching(false)
          })
      } else {
        this.snapshotWizardState()
      }
    })
  },

  methods: {
    ...mapActions([
      'setFetching',
      'setFeedback',
      'attemptGetThematicPage',
      'attemptGetThematicPagesList',
      'attemptCreateThematicPage',
      'attemptUpdateThematicPage',
      'attemptGetSolutionsList',
      'attemptGetLearningPathsList',
      'attemptGetProgramsList',
      'attemptGetToolsList',
      'attemptToggleActiveThematicPage',
      'attemptGetThematicPageTypesList',
      'attemptGetPartnersList'
    ]),

    updateFormattedUrl () {
      if (this.formData.thematicPageTypeId) {
        const found = this.thematicPageTypesList
          .filter((item) => item.value === this.formData.thematicPageTypeId)[0]

        if (!found) return

        this.formatedUrl = `${process.env.VUE_APP_PORTAL_URL}${found.text.toLowerCase()}/${this.formData.slug || ''}`
      } else {
        this.formatedUrl = `${process.env.VUE_APP_PORTAL_URL}pagina-tematica/${this.formData.slug || ''}`
      }
    },

    processThematicPageContents (data) {
      const contents = data.thematicPageContents || []

      this.formData.thematicPageSolutions = []
      this.formData.thematicPageLearningPaths = []
      this.formData.thematicPagePrograms = []
      this.formData.thematicPageTools = []
      this.formData.thematicPageContents = []

      const sortedContents = [ ...contents ].sort((a, b) => (a.sorting || 0) - (b.sorting || 0))

      sortedContents.forEach(content => {
        const type = content.solution_type || content.solutionType

        const item = {
          id: content.id,
          name: content.title || content.name,
          solutionType: type,
          sorting: content.sorting
        }

        this.formData.thematicPageContents.push(item)

        if (type === 'solution') {
          this.formData.thematicPageSolutions.push({
            id: content.id,
            name: content.title || content.name
          })
        } else if (type === 'learning_path') {
          this.formData.thematicPageLearningPaths.push({
            id: content.id,
            name: content.title || content.name
          })
        } else if (type === 'program') {
          this.formData.thematicPagePrograms.push({
            id: content.id,
            name: content.title || content.name
          })
        } else if (type === 'tool') {
          this.formData.thematicPageTools.push({
            id: content.id,
            title: content.title || content.name
          })
        }
      })

      this.showSolutions = this.formData.thematicPageSolutions.length > 0
      this.showPaths = this.formData.thematicPageLearningPaths.length > 0
      this.showPrograms = this.formData.thematicPagePrograms.length > 0
      this.showTools = this.formData.thematicPageTools.length > 0
    },

    getFeaturedContentConfig () {
      return {
        solution: {
          dataKey: 'solution',
          arrayKey: 'thematicPageSolutions',
          solutionType: 'solution',
          showFlag: 'showSolutions',
          nameKey: 'name'
        },

        learning_path: {
          dataKey: 'learningPath',
          arrayKey: 'thematicPageLearningPaths',
          solutionType: 'learning_path',
          showFlag: 'showPaths',
          nameKey: 'name'
        },

        program: {
          dataKey: 'program',
          arrayKey: 'thematicPagePrograms',
          solutionType: 'program',
          showFlag: 'showPrograms',
          nameKey: 'name'
        },

        tool: {
          dataKey: 'tool',
          arrayKey: 'thematicPageTools',
          solutionType: 'tool',
          showFlag: 'showTools',
          nameKey: 'title'
        }
      }
    },

    addFeaturedContentToArrays (contentData, actualId, config) {
      const targetArray = this.formData[config.arrayKey]
      const alreadyExists = targetArray.find(item => item.id === actualId)

      if (alreadyExists) return

      targetArray.push({
        id: actualId,
        [config.nameKey]: contentData.name || contentData.title
      })

      this.formData.thematicPageContents.push({
        id: actualId,
        name: contentData.name || contentData.title,
        solutionType: config.solutionType,
        sorting: this.formData.thematicPageContents.length + 1
      })

      this[config.showFlag] = true
    },

    processFeaturedContent (featured) {
      const configMap = this.getFeaturedContentConfig()
      const config = configMap[featured.contentType]

      if (!config) {
        return {
          contentData: null,
          actualId: null
        }
      }

      const contentData = featured[config.dataKey]

      if (!contentData) {
        return {
          contentData: null,
          actualId: null
        }
      }

      const actualId = contentData.id

      this.addFeaturedContentToArrays(contentData, actualId, config)

      return {
        contentData,
        actualId
      }
    },

    processFeaturedContents (data) {
      if (data.thematicPageFeaturedContents && Array.isArray(data.thematicPageFeaturedContents)) {
        this.formData.featuredContents = data.thematicPageFeaturedContents.map(featured => {
          const { contentData, actualId } = this.processFeaturedContent(featured)

          return {
            id: actualId,
            name: contentData ? (contentData.name || contentData.title) : `Conteúdo ${featured.id}`,
            contentType: featured.contentType,
            sorting: featured.sorting
          }
        }).filter(item => item.id !== null)

        this.formData.featuredContents.sort((a, b) => (a.sorting || 0) - (b.sorting || 0))
      }

      this.showFeaturedContents = !!(this.formData.featuredContents && this.formData.featuredContents.length > 0)
    },

    getValidationFieldsByTab () {
      return {
        1: [
          this.$v.formData.thematicPageTypeId,
          this.$v.formData.partnerId,
          this.$v.formData.name,
          this.$v.formData.slug,
          this.$v.formData.subtitle,
          this.$v.formData.callText,
          this.$v.formData.description,
          this.$v.formData.imageBanner,
          this.$v.formData.imageLogo,
          this.$v.formData.showList,
          this.$v.formData.facebook,
          this.$v.formData.instagram,
          this.$v.formData.twitter,
          this.$v.formData.linkedin,
          this.$v.formData.restrictedAccess,
          this.$v.formData.restrictedAccessTitle,
          this.$v.formData.restrictedAccessText,
          this.$v.formData.accessCode,
          this.$v.formData.privacyNotice,
          this.$v.formData.privacyNoticeDescription,
          this.$refs && this.$refs.colorsSection && this.$refs.colorsSection.$v
        ],
        2: [],
        3: [],
        4: []
      }
    },

    wizardTrackedState () {
      return {
        formData: this.formData,
        sortingTypeContents: this.sortingTypeContents,
        sortingTypeFeatured: this.sortingTypeFeatured,
        showFeaturedContents: this.showFeaturedContents
      }
    },

    prevPage () {
      if (this.activeTab > 1) this.activeTab -= 1
    },

    nextPage () {
      if (!this.validateActiveTab()) {
        this.setFeedback({ message: this.$t('thematicPages:feedback.validation.error') })

        return
      }

      if (this.activeTab < this.maxStepsCount) {
        this.activeTab += 1
      }

      if (this.activeTab === 3) {
        if (this.solutionsPagination.query.name) {
          this.solutionsPagination.query.name = null
          this.solutionsPagination.page = 1
          this.fetchSolutionsData()
        }

        if (this.pathsPagination.query.name) {
          this.pathsPagination.query.name = null
          this.pathsPagination.page = 1
          this.fetchPathsData()
        }

        if (this.programsPagination.query.name) {
          this.programsPagination.query.name = null
          this.programsPagination.page = 1
          this.fetchProgramsData()
        }

        if (this.toolsPagination.query.title) {
          this.toolsPagination.query.title = null
          this.toolsPagination.page = 1
          this.fetchToolsData()
        }
      }
    },

    syncUnifiedContents () {
      const existingContents = this.formData.thematicPageContents || []
      const existingMap = new Map()

      existingContents.forEach((content, index) => {
        const key = `${content.solutionType || content.solution_type}-${content.id}`

        existingMap.set(key, {
          ...content,
          sorting: content.sorting || index
        })
      })

      const newContents = []

      if (this.showSolutions && this.formData.thematicPageSolutions) {
        this.formData.thematicPageSolutions.forEach(solution => {
          const key = `solution-${solution.id}`
          const existing = existingMap.get(key)

          newContents.push({
            id: solution.id,
            name: solution.name,
            solutionType: 'solution',
            sorting: existing ? existing.sorting : 9999
          })
        })
      }

      if (this.showPaths && this.formData.thematicPageLearningPaths) {
        this.formData.thematicPageLearningPaths.forEach(path => {
          const key = `learning_path-${path.id}`
          const existing = existingMap.get(key)

          newContents.push({
            id: path.id,
            name: path.name,
            solutionType: 'learning_path',
            sorting: existing ? existing.sorting : 9999
          })
        })
      }

      if (this.showPrograms && this.formData.thematicPagePrograms) {
        this.formData.thematicPagePrograms.forEach(program => {
          const key = `program-${program.id}`
          const existing = existingMap.get(key)

          newContents.push({
            id: program.id,
            name: program.name,
            solutionType: 'program',
            sorting: existing ? existing.sorting : 9999
          })
        })
      }

      if (this.showTools && this.formData.thematicPageTools) {
        this.formData.thematicPageTools.forEach(tool => {
          const key = `tool-${tool.id}`
          const existing = existingMap.get(key)

          newContents.push({
            id: tool.id,
            name: tool.title,
            solutionType: 'tool',
            sorting: existing ? existing.sorting : 9999
          })
        })
      }

      newContents.sort((a, b) => a.sorting - b.sorting)

      newContents.forEach((content, index) => {
        content.sorting = index + 1
      })

      this.formData.thematicPageContents = newContents
    },

    formatFormData () {
      const data = {}

      data.id_thematic_page = this.formData.id
      data.thematic_page_type_id = this.formData.thematicPageTypeId
      data.image_logo = this.formData.imageLogo
      data.image_banner = this.formData.imageBanner
      data.image_favicon = this.formData.favicon
      data.name = this.formData.name
      data.subtitle = this.formData.subtitle
      data.call_text = this.formData.callText
      data.description = this.formData.description
      data.show = this.formData.showList
      data.color_bottom = this.formData.colorBottom
      data.color_header = this.formData.colorHeader || ''
      data.color_menu = this.formData.colorMenu
      data.slug = this.formData.slug
      data.seoTitle = this.formData.seoTitle
      data.seoDescription = this.formData.seoDescription
      data.seoKeywords = this.formData.seoKeywords
      data.facebook = this.formData.facebook
      data.instagram = this.formData.instagram
      data.linkedin = this.formData.linkedin
      data.twitter = this.formData.twitter

      data.sorting_type_solution = this.sortingTypeContents
      data.sorting_type_learning_path = this.sortingTypeContents
      data.sorting_type_program = this.sortingTypeContents
      data.sorting_type_tool = this.sortingTypeContents

      data.restricted_access = this.formData.restrictedAccess
      data.restricted_access_title = this.formData.restrictedAccess ? this.formData.restrictedAccessTitle : null
      data.restricted_access_text = this.formData.restrictedAccess ? this.formData.restrictedAccessText : null
      data.access_code = this.formData.restrictedAccess ? this.formData.accessCode : null
      data.privacy_notice = this.formData.privacyNotice
      data.privacy_notice_description = this.formData.privacyNotice ? this.formData.privacyNoticeDescription : null
      data.is_creci_sp = this.formData.isCreciSp
      data.show_on_vitrine = Boolean(this.formData.showOnVitrine)
      data.partner_id = this.formData.partnerId
      data.is_repasse = this.formData.isRepasse

      data.thematicPageContents = this.formData.thematicPageContents
        .map((content, index) => ({
          id: content.id,
          solution_type: content.solutionType,
          sorting: index + 1
        }))

      data.featured_contents = this.showFeaturedContents
        ? this.formData.featuredContents.map((content, index) => ({
          id: content.id,
          content_type: content.contentType,
          sorting: index + 1
        }))
        : []

      return data
    },

    submitCreation () {
      this.setFetching(true)

      return this.attemptCreateThematicPage(this.formatFormData())
        .then(({ data }) => {
          this.formData.id = data.id
          this.snapshotWizardState()
          this.setFeedback({ message: this.$t('theme.pages:submit.create.success') })

          return true
        })
        .catch((error) => {
          this.setSubmitErrorFeedback(error)

          return false
        })
        .finally(() => {
          this.setFetching(false)
        })
    },

    submitUpdate () {
      this.setFetching(true)

      return this.attemptUpdateThematicPage(this.formatFormData())
        .then(({ data }) => {
          this.formData = {
            ...this.formData,
            ...data
          }

          this.formData.thematicPageTypeId = data.thematicPageType.id
          this.formData.showOnVitrine = Boolean(data.showOnVitrine ?? this.formData.showOnVitrine ?? false)
          this.formData.restrictedAccess = !!this.formData.restrictedAccess
          this.formData.restrictedAccessText = this.formData.restrictedAccessText?.length ? this.formData.restrictedAccessText : this.$t('global:form.thematicPages.accesscode.restricted.access.text.default')
          this.formData.restrictedAccessTitle = this.formData.restrictedAccessTitle?.length ? this.formData.restrictedAccessTitle : this.$t('global:form.thematicPages.accesscode.restricted.access.title.text.default')
          this.formData.privacyNotice = !!this.formData.privacyNotice
          this.formData.privacyNoticeDescription = this.formData.privacyNoticeDescription?.length ? this.formData.privacyNoticeDescription : this.$t('global:form.thematicPages.privacyNotice.text.default')

          this.processThematicPageContents(data)
          this.processFeaturedContents(data)

          if (data.sortingTypeSolution !== undefined && data.sortingTypeSolution !== null) {
            this.sortingTypeContents = (data.sortingTypeSolution === true || data.sortingTypeSolution === 'manual') ? 'manual' : 'default'
          }

          if (data.sortingTypeFeatured !== undefined && data.sortingTypeFeatured !== null) {
            this.sortingTypeFeatured = (data.sortingTypeFeatured === true || data.sortingTypeFeatured === 'manual') ? 'manual' : 'default'
          }

          this.snapshotWizardState()
          this.setFeedback({ message: this.$t('theme.pages:submit.update.success') })

          return true
        })
        .catch((error) => {
          this.setSubmitErrorFeedback(error)

          return false
        })
        .finally(() => {
          this.setFetching(false)
        })
    },

    clearLinkedSolutions (event) {
      if (event) return

      this.formData.thematicPageSolutions = []
      this.clearUnifiedContentsOfType('solution')
    },

    addToLinkedSolution (solutionId) {
      if (this.formData.thematicPageSolutions.some((solution) => solution.id === solutionId)) {
        return
      }

      const solution = this.solutions.find((solution) => solution.id === solutionId)

      if (solution) {
        this.formData.thematicPageSolutions.push(solution)
        this.addToUnifiedContents(solution.id, solution.name, 'solution')
      }
    },

    clearLinkedPaths (event) {
      // O valor do toggle chega por $event: true liga a secao, false desliga.
      // Sem ele o metodo limpava a colecao tambem ao LIGAR, e nos casos sem
      // parametro `event` caia no window.event global, que e truthy durante o
      // clique e impedia qualquer limpeza.
      if (event) return

      this.formData.thematicPageLearningPaths = []
      this.clearUnifiedContentsOfType('learning_path')
    },

    addToLinkedPath (pathId) {
      if (this.formData.thematicPageLearningPaths.some((path) => path.id === pathId)) {
        return
      }

      const path = this.paths.find((path) => path.id === pathId)

      if (path) {
        this.formData.thematicPageLearningPaths.push(path)
        this.addToUnifiedContents(path.id, path.name, 'learning_path')
      }
    },

    clearLinkedPrograms (event) {
      if (event) return

      this.formData.thematicPagePrograms = []
      this.clearUnifiedContentsOfType('program')
    },

    addToLinkedProgram (programId) {
      if (this.formData.thematicPagePrograms.some((program) => program.id === programId)) {
        return
      }

      const program = this.programs.find((program) => program.id === programId)

      if (program) {
        this.formData.thematicPagePrograms.push(program)
        this.addToUnifiedContents(program.id, program.name, 'program')
      }
    },

    addToUnifiedContents (id, name, solutionType) {
      const jaExiste = this.formData.thematicPageContents.some(
        content => content.id === id && content.solutionType === solutionType
      )

      if (jaExiste) return

      this.formData.thematicPageContents.push({
        id,
        name,
        solutionType,
        sorting: this.formData.thematicPageContents.length + 1
      })
    },

    removeFromUnifiedContents (id, solutionType) {
      this.formData.thematicPageContents = this.formData.thematicPageContents.filter(
        content => !(content.id === id && content.solutionType === solutionType)
      )

      this.updateUnifiedContentsSorting()
    },

    clearUnifiedContentsOfType (solutionType) {
      this.formData.thematicPageContents = this.formData.thematicPageContents.filter(
        content => content.solutionType !== solutionType
      )

      this.updateUnifiedContentsSorting()
    },

    setSubmitErrorFeedback (error) {
      if (!error || !error.data || !error.data.error) {
        this.setFeedback({ message: this.$t('global.error') })

        return
      }

      const { code, message } = error.data.error

      if (code === 403 && message === 'access_not_allowed') {
        return
      }

      const knownErrors = [
        'partner_required_for_corporate_type',
        'is_repasse_requires_partner',
        'partner_not_found'
      ]

      if (knownErrors.includes(message)) {
        this.setFeedback({ message: this.$t(`thematicPages:feedback.error.${message}`) })

        return
      }

      this.setFeedback({ message: this.$t('global.error') })
    },

    focusFirstInvalidField () {
      this.activeTab = 1

      this.$nextTick(() => {
        const hasErrorElement = this.$el.querySelector('.has-error')

        if (!hasErrorElement) return

        const top = hasErrorElement.getBoundingClientRect().top + window.pageYOffset - 120

        window.scroll({
          top: top > 0 ? top : 0,
          behavior: 'smooth'
        })
      })
    },

    submit () {
      this.$v.$touch()

      let colorsInvalid = false

      if (this.$refs && this.$refs.colorsSection && this.$refs.colorsSection.$v) {
        this.$refs.colorsSection.$v.$touch()
        colorsInvalid = this.$refs.colorsSection.$v.$invalid
      }

      if (!this.$v.$invalid && !colorsInvalid) {
        this.syncUnifiedContents()

        return this.isEditing ? this.submitUpdate() : this.submitCreation()
      }

      this.focusFirstInvalidField()
      this.setFeedback({ message: this.$t('thematicPages:feedback.validation.error') })

      return Promise.resolve(false)
    },

    leave () {
      this.$router.push(this.backUrl)
    },

    setup () {
      this.TRANSLATION = TRANSLATION
      this.RICH_TEXT_EDITOR_TOOLBAR = RICH_TEXT_EDITOR_TOOLBAR
      this.setFetching(true)

      return Promise.all([
        this.attemptGetSolutionsList(this.solutionsPagination).then((pagination) => {
          if (pagination.data) {
            this.solutions = []
            this.solutionsPagination.lastPage = pagination.lastPage
            this.solutionsPagination.page = pagination.actualPage

            pagination.data.forEach((element) => {
              this.solutions.push({
                name: element.name,
                id: element.id,
                workload: element.workload,
                solutionType: element.solutionType,
                showOnPortal: Boolean(element.showOnPortal)
              })
            })
          }
        }),

        this.attemptGetLearningPathsList(this.pathsPagination).then((pagination) => {
          if (pagination.data) {
            this.paths = []
            this.pathsPagination.lastPage = pagination.lastPage
            this.pathsPagination.page = pagination.actualPage

            pagination.data.forEach((element) => {
              this.paths.push({
                name: element.name,
                id: element.id,
                learningPathSolutions: element.learningPathSolutions.length,
                solutionType: element.solutionType,
                workload: element.workload,
                showOnPortal: Boolean(element.showOnPortal)
              })
            })
          }
        }),

        this.attemptGetProgramsList(this.programsPagination).then((pagination) => {
          if (pagination.data) {
            this.programs = []
            this.programsPagination.lastPage = pagination.lastPage
            this.programsPagination.page = pagination.actualPage

            pagination.data.forEach((element) => {
              this.programs.push({
                name: element.name,
                id: element.id,
                learningPathSolutions: element.programSolutions.length,
                solutionType: element.solutionType,
                showOnPortal: Boolean(element.showOnPortal),
                workload: element.workload
              })
            })
          }
        }),

        this.attemptGetToolsList(this.toolsPagination).then((pagination) => {
          if (pagination.data && pagination.data.data) {
            this.tools = []
            this.toolsPagination.lastPage = pagination.data.lastPage
            this.toolsPagination.page = pagination.data.actualPage

            pagination.data.data.forEach((element) => {
              this.tools.push({
                title: element.title,
                id: element.id,
                active: Boolean(element.active)
              })
            })
          }
        }),

        this.attemptGetThematicPageTypesList({
          page: 1,
          limit: 100
        }).then((pagination) => {
          if (pagination.data) {
            pagination.data.forEach((element) => {
              this.thematicPageTypesList.push({
                text: element.name,
                value: element.id,
                alias: element.alias
              })
            })

            return true
          }
        }),

        this.attemptGetPartnersList({
          page: 1,
          limit: 200
        }).then((pagination) => {
          if (pagination.data) {
            this.partnersListOptions = pagination.data.map((p) => ({
              text: p.name,
              value: p.id
            }))
          }
        })
      ]).finally(() => {
        this.setFetching(false)
      })
    },

    saveAll () {
      if (this.isEditing && !this.formData.active) {
        this.modalActiveThematicPageOpen = true
      } else {
        this.submit(false)
      }
    },

    saveAllConfirm () {
      if (this.isEditing) {
        this.modalActiveThematicPageOpen = false

        this.submit(false).then(saved => {
          if (!saved) return

          this.attemptToggleActiveThematicPage(this.formData.id).then(() => {
            this.setFeedback({ message: this.$t('thematicPages:feedback.toggle.activate.success') })
          })
        })
      }
    },

    saveAllCancel () {
      this.modalActiveThematicPageOpen = false
      this.submit(false)
    },

    searchSolution (search) {
      this.solutionsPagination.query.name = search
      this.solutionsPagination.page = 1
      this.solutionsPagination.lastPage = null
      this.solutionsPagination.query.only_name = true
      this.fetchSolutionsData()
    },

    loadMoreSolutions () {
      this.isLoadingMoreSolutions = true

      if (this.solutionsPagination.lastPage) {
        if (this.solutionsPagination.lastPage > this.solutionsPagination.page) {
          this.solutionsPagination.page += 1
        } else {
          this.isLoadingMoreSolutions = false

          return
        }
      }

      this.fetchSolutionsData(true).then(() => {
        this.isLoadingMoreSolutions = false
      })
    },

    async fetchSolutionsData (isLoadMore = false) {
      this.setFetching(true)

      return this.attemptGetSolutionsList(this.solutionsPagination).then((pagination) => {
        this.solutionInfiniteScrollId += 1
        this.solutionsPagination.lastPage = pagination.lastPage
        this.solutionsPagination.page = pagination.actualPage

        if (pagination.data) {
          if (!isLoadMore) {
            this.solutions = []
          }

          pagination.data.forEach((element) => {
            this.solutions.push({
              name: element.name,
              id: element.id,
              workload: element.workload,
              solutionType: element.solutionType,
              showOnPortal: Boolean(element.showOnPortal)
            })
          })
        }

        this.setFetching(false)

        return pagination
      })
    },

    searchPath (search) {
      this.pathsPagination.query.name = search
      this.pathsPagination.page = 1
      this.pathsPagination.lastPage = null
      this.fetchPathsData()
    },

    loadMorePaths () {
      this.isLoadingMorePaths = true

      if (this.pathsPagination.lastPage) {
        if (this.pathsPagination.lastPage > this.pathsPagination.page) {
          this.pathsPagination.page += 1
        } else {
          this.isLoadingMorePaths = false

          return
        }
      }

      this.fetchPathsData(true).then(() => {
        this.isLoadingMorePaths = false
      })
    },

    async fetchPathsData (isLoadMore = false) {
      this.setFetching(true)

      return this.attemptGetLearningPathsList(this.pathsPagination).then((pagination) => {
        this.pathsInfiniteScrollId += 1
        this.pathsPagination.lastPage = pagination.lastPage
        this.pathsPagination.page = pagination.actualPage

        if (pagination.data) {
          if (!isLoadMore) {
            this.paths = []
          }

          pagination.data.forEach((element) => {
            this.paths.push({
              name: element.name,
              id: element.id,
              learningPathSolutions: element.learningPathSolutions.length,
              workload: element.workload,
              showOnPortal: Boolean(element.showOnPortal)
            })
          })
        }

        this.setFetching(false)

        return pagination
      })
    },

    getSolutionTypeFormattedName (solutionType) {
      return solutionTypeFormatter(solutionType)
    },

    searchProgram (search) {
      this.programsPagination.query.name = search
      this.programsPagination.page = 1
      this.programsPagination.lastPage = null
      this.fetchProgramsData()
    },

    loadMorePrograms () {
      this.isLoadingMorePrograms = true

      if (this.programsPagination.lastPage) {
        if (this.programsPagination.lastPage > this.programsPagination.page) {
          this.programsPagination.page += 1
        } else {
          this.isLoadingMorePrograms = false

          return
        }
      }

      this.fetchProgramsData(true).then(() => {
        this.isLoadingMorePrograms = false
      })
    },

    async fetchProgramsData (isLoadMore = false) {
      this.setFetching(true)

      return this.attemptGetProgramsList(this.programsPagination).then((pagination) => {
        this.programsInfiniteScrollId += 1
        this.programsPagination.lastPage = pagination.lastPage
        this.programsPagination.page = pagination.actualPage

        if (pagination.data) {
          if (!isLoadMore) {
            this.programs = []
          }

          pagination.data.forEach((element) => {
            this.programs.push({
              name: element.name,
              id: element.id,
              programSolutions: element.programSolutions.length,
              workload: element.workload,
              showOnPortal: Boolean(element.showOnPortal)
            })
          })
        }

        this.setFetching(false)

        return pagination
      })
    },

    searchTool (search) {
      this.toolsPagination.query.title = search
      this.toolsPagination.page = 1
      this.toolsPagination.lastPage = null
      this.fetchToolsData()
    },

    loadMoreTools () {
      this.isLoadingMoreTools = true

      if (this.toolsPagination.lastPage) {
        if (this.toolsPagination.lastPage > this.toolsPagination.page) {
          this.toolsPagination.page += 1
        } else {
          this.isLoadingMoreTools = false

          return
        }
      }

      this.fetchToolsData(true).then(() => {
        this.isLoadingMoreTools = false
      })
    },

    async fetchToolsData (isLoadMore = false) {
      this.setFetching(true)

      return this.attemptGetToolsList(this.toolsPagination).then((pagination) => {
        this.toolsInfiniteScrollId += 1
        this.toolsPagination.lastPage = pagination.data.lastPage
        this.toolsPagination.page = pagination.data.actualPage

        if (pagination.data && pagination.data.data) {
          if (!isLoadMore) {
            this.tools = []
          }

          pagination.data.data.forEach((element) => {
            const alreadyLoaded = this.tools.some((tool) => tool.id === element.id)

            if (!alreadyLoaded) {
              this.tools.push({
                title: element.title,
                id: element.id,
                active: Boolean(element.active)
              })
            }
          })
        }

        this.setFetching(false)

        return pagination
      })
    },

    removeLinkedSolution (solutionId) {
      this.formData.thematicPageSolutions = this.formData.thematicPageSolutions.filter(
        (solution) => solution.id !== solutionId
      )

      this.removeFromUnifiedContents(solutionId, 'solution')

      if (this.formData.thematicPageSolutions.length === 0) {
        this.showSolutions = false
      }
    },

    removeLinkedPath (pathId) {
      this.formData.thematicPageLearningPaths = this.formData.thematicPageLearningPaths.filter(
        (path) => path.id !== pathId
      )

      this.removeFromUnifiedContents(pathId, 'learning_path')

      if (this.formData.thematicPageLearningPaths.length === 0) {
        this.showPaths = false
      }
    },

    removeLinkedProgram (programId) {
      this.formData.thematicPagePrograms = this.formData.thematicPagePrograms.filter(
        (program) => program.id !== programId
      )

      this.removeFromUnifiedContents(programId, 'program')

      if (this.formData.thematicPagePrograms.length === 0) {
        this.showPrograms = false
      }
    },

    moveUnifiedContentUp (content) {
      const index = this.formData.thematicPageContents.findIndex(
        c => c.id === content.id && c.solutionType === content.solutionType
      )

      if (index > 0) {
        const swapContent = this.formData.thematicPageContents[index - 1]

        this.$set(this.formData.thematicPageContents, index - 1, this.formData.thematicPageContents[index])
        this.$set(this.formData.thematicPageContents, index, swapContent)
        this.updateUnifiedContentsSorting()
      }
    },

    moveUnifiedContentDown (content) {
      const index = this.formData.thematicPageContents.findIndex(
        c => c.id === content.id && c.solutionType === content.solutionType
      )

      if (index < this.formData.thematicPageContents.length - 1) {
        const swapContent = this.formData.thematicPageContents[index + 1]

        this.$set(this.formData.thematicPageContents, index + 1, this.formData.thematicPageContents[index])
        this.$set(this.formData.thematicPageContents, index, swapContent)
        this.updateUnifiedContentsSorting()
      }
    },

    clearLinkedTools (event) {

      if (event) return

      this.formData.thematicPageTools = []

      this.formData.thematicPageContents = this.formData.thematicPageContents.filter(
        c => c.solutionType !== 'tool'
      )

      this.updateUnifiedContentsSorting()
    },

    addToLinkedTool (toolId) {
      if (this.formData.thematicPageTools.some((tool) => tool.id === toolId)) {
        return
      }

      const tool = this.tools.find((tool) => tool.id === toolId)

      if (tool) {
        this.formData.thematicPageTools.push(tool)

        this.formData.thematicPageContents.push({
          id: tool.id,
          name: tool.title,
          solutionType: 'tool',
          sorting: this.formData.thematicPageContents.length + 1
        })
      }
    },

    removeLinkedTool (toolId) {
      this.formData.thematicPageTools = this.formData.thematicPageTools.filter(
        (tool) => tool.id !== toolId
      )

      this.formData.thematicPageContents = this.formData.thematicPageContents.filter(
        c => !(c.id === toolId && c.solutionType === 'tool')
      )

      if (this.formData.thematicPageTools.length === 0) {
        this.showTools = false
      }

      this.updateUnifiedContentsSorting()
    },

    removeUnifiedContent (content) {
      this.formData.thematicPageContents = this.formData.thematicPageContents.filter(
        c => !(c.id === content.id && c.solutionType === content.solutionType)
      )

      if (content.solutionType === 'solution') {
        this.formData.thematicPageSolutions = this.formData.thematicPageSolutions.filter(s => s.id !== content.id)

        if (this.formData.thematicPageSolutions.length === 0) {
          this.showSolutions = false
        }
      } else if (content.solutionType === 'learning_path') {
        this.formData.thematicPageLearningPaths = this.formData.thematicPageLearningPaths.filter(p => p.id !== content.id)

        if (this.formData.thematicPageLearningPaths.length === 0) {
          this.showPaths = false
        }
      } else if (content.solutionType === 'program') {
        this.formData.thematicPagePrograms = this.formData.thematicPagePrograms.filter(p => p.id !== content.id)

        if (this.formData.thematicPagePrograms.length === 0) {
          this.showPrograms = false
        }
      } else if (content.solutionType === 'tool') {
        this.formData.thematicPageTools = this.formData.thematicPageTools.filter(t => t.id !== content.id)

        if (this.formData.thematicPageTools.length === 0) {
          this.showTools = false
        }
      }

      this.updateUnifiedContentsSorting()
    },

    updateUnifiedContentsSorting () {
      this.formData.thematicPageContents.forEach((content, index) => {
        content.sorting = index + 1
      })
    },

    updateFeaturedContentsSorting () {
      this.formData.featuredContents.forEach((content, index) => {
        content.sorting = index + 1
      })
    },

    addToFeaturedContents (content) {
      if (this.formData.featuredContents.length >= 5) {
        this.setFeedback({ message: this.$t('thematicPages.featured:max.limit.reached') })

        return
      }

      const isAlreadyFeatured = this.formData.featuredContents.find(featured =>
        featured.id === content.id && featured.contentType === content.contentType
      )

      if (isAlreadyFeatured) {
        return
      }

      this.formData.featuredContents.push({
        id: content.id,
        name: content.name,
        contentType: content.contentType
      })
    },

    removeFromFeaturedContents (content) {
      this.formData.featuredContents = this.formData.featuredContents.filter(featured =>
        !(featured.id === content.id && featured.contentType === content.contentType)
      )
    },

    moveFeaturedContentUp (content) {
      const index = this.formData.featuredContents.findIndex(featured =>
        featured.id === content.id && featured.contentType === content.contentType
      )

      if (index > 0) {
        const swapContent = this.formData.featuredContents[index - 1]

        this.$set(this.formData.featuredContents, index - 1, this.formData.featuredContents[index])
        this.$set(this.formData.featuredContents, index, swapContent)

        this.updateFeaturedContentsSorting()
      }
    },

    moveFeaturedContentDown (content) {
      const index = this.formData.featuredContents.findIndex(featured =>
        featured.id === content.id && featured.contentType === content.contentType
      )

      if (index < this.formData.featuredContents.length - 1) {
        const swapContent = this.formData.featuredContents[index + 1]

        this.$set(this.formData.featuredContents, index + 1, this.formData.featuredContents[index])
        this.$set(this.formData.featuredContents, index, swapContent)

        this.updateFeaturedContentsSorting()
      }
    },

    getContentTypeLabel (contentType) {
      const labels = {
        solution: this.$t('global:form.solution'),
        learning_path: this.$t('global:form.learning.path'),
        program: this.$t('global:form.program'),
        tool: this.$t('global:form.tool')
      }

      return labels[contentType] || contentType
    },

    clearFeaturedContents (event) {
      if (event) return

      this.formData.featuredContents = []
    },

    cleanupFeaturedContents () {
      this.formData.featuredContents = this.formData.featuredContents.filter(featured => {
        return this.linkedContents.find(linked =>
          linked.id === featured.id && linked.contentType === featured.contentType
        )
      })
    }
  }
}
</script>

<template>
  <div class="main-content thematic-pages-create">
    <ContentHeader
      :title="isEditing ? formData.name : $t('thematicPages.create:header.title')"
      dark-theme
      sticky
    >
      <Action
        slot="back"
        type="button"
        :text="$t('global:back.thematicPages')"
        class="btn-back"
        icon="keyboard_backspace"
        @click="leave()"
      />
      <ActionBar slot="actionbar" />
      <template slot="buttons">
        <Action
          :text="$t('global:form.save')"
          type="button"
          flat
          dark
          @click="saveAll()"
        />
      </template>
    </ContentHeader>
    <div
      v-if="activeTab === 1"
      class="main-content-inner"
    >
      <div class="center">
        <div class="thematic-pages-create-header">
          <h2 class="thematic-pages-create-title">
            {{ $t("global:form.step", { num: 1 }) }}
          </h2>
          <p
            class="thematic-pages-create-description"
            v-html="$t('thematicPages.create:header.description')"
          />
        </div>
        <form
          class="form"
          @submit.prevent="submit()"
        >
          <SelectField
            v-model="formData.thematicPageTypeId"
            tabindex="0"
            :label="$t('global:form.thematicPages.type')"
            :items="thematicPageTypesList"
            :validation="$v.formData.thematicPageTypeId"
          />

          <InputField
            v-model="formData.name"
            :label="$t('global:form.thematicPages.name')"
            :validation="$v.formData.name"
            :counter="100"
          />

          <InputField
            v-model="formData.subtitle"
            :label="$t('global:form.thematicPages.slogan')"
            :validation="$v.formData.subtitle"
            :counter="250"
          />

          <InputField
            v-model="formData.slug"
            :label="$t('global:form.thematicPages.slug')"
            :validation="$v.formData.slug"
            :counter="100"
            :under-description="formatedUrl"
          />

          <InputField
            v-model="formData.callText"
            :label="$t('global:form.thematicPages.main.title')"
            :validation="$v.formData.callText"
            :counter="250"
          />

          <FormSection :title="TRANSLATION.description.description">
            <RichTextEditor
              v-model="formData.description"
              :validation="$v.formData.description"
              :toolbar="RICH_TEXT_EDITOR_TOOLBAR"
              :max-length="formData.thematicPageAlias === 'corporative' ? 600 : 2500"
              class="rich-text"
            />
          </FormSection>

          <FormSection :title="$t('global:upload.add.thematicPage.banner')">
            <Upload
              v-model="formData.imageBanner"
              icon="image-multiple"
              :label="$t('global:upload.add.image')"
              :description="$t('global:upload.add.thematicPage.banner.description')"
              cropper
              :width="2740"
              :height="942"
              :preview="0.175"
              :validation="$v.formData.imageBanner"
            />
          </FormSection>

          <FormSection :title="$t('global:upload.add.thematicPage.background')">
            <Upload
              v-model="formData.imageLogo"
              icon="image-multiple"
              :label="$t('global:upload.add.image')"
              :description="$t('global:upload.add.thematicPage.background.description')"
              cropper
              :width="327"
              :height="327"
              :preview="0.6"
              :validation="$v.formData.imageLogo"
            />
          </FormSection>

          <FormSection
            v-if="formData.thematicPageAlias === 'corporative'"
            :key="formData.thematicPageTypeId"
            :title="$t('global:upload.add.thematicPage.favicon')"
          >
            <Upload
              v-model="formData.favicon"
              icon="image-multiple"
              :label="$t('global:upload.add.image')"
              :description="$t('global:upload.add.thematicPage.favicon.description')"
              cropper
              :width="53"
              :height="53"
              :preview="3"
              :validation="$v.formData.favicon"
            />
          </FormSection>

          <FormSection :title="$t('global:form.thematicPages.socials')">
            <InputField
              v-model="formData.facebook"
              :label="$t('global:form.thematicPages.socials.facebook')"
              :validation="$v.formData.facebook"
              :counter="250"
              type="url"
              :under-description="$t('global:form.thematicPages.socials.facebook.url')"
            />

            <InputField
              v-model="formData.instagram"
              :label="$t('global:form.thematicPages.socials.instagram')"
              :validation="$v.formData.instagram"
              :counter="250"
              type="url"
              :under-description="$t('global:form.thematicPages.socials.instagram.url')"
            />

            <InputField
              v-model="formData.linkedin"
              :label="$t('global:form.thematicPages.socials.linkedin')"
              :validation="$v.formData.linkedin"
              :counter="250"
              type="url"
              :under-description="$t('global:form.thematicPages.socials.linkedin.url')"
            />

            <InputField
              v-model="formData.twitter"
              :label="$t('global:form.thematicPages.socials.twitter')"
              :validation="$v.formData.twitter"
              :counter="250"
              type="url"
              :under-description="$t('global:form.thematicPages.socials.twitter.url')"
            />
          </FormSection>

          <ThematicPagesColors
            ref="colorsSection"
            :key="`colors-${formData.thematicPageAlias || 'unknown'}`"
            :alias="formData.thematicPageAlias"
            :color-header.sync="formData.colorHeader"
            :color-bottom.sync="formData.colorBottom"
            :color-menu.sync="formData.colorMenu"
          />

          <FormSection
            v-if="formData.thematicPageAlias === 'corporative'"
            :title="$t('global:form.thematicPages.partner')"
          >
            <SelectField
              v-model="formData.partnerId"
              :label="$t('global:form.thematicPages.partner.placeholder')"
              :items="partnersListOptions"
              :validation="$v.formData.partnerId"
            />
          </FormSection>

          <FormSection :title="$t('global:form.thematicPages.repasse')">
            <Radio
              v-model="formData.isRepasse"
              :items="yesNoOptions"
              :under-description="$t('global:form.thematicPages.repasse.description')"
              horizontal
            />
          </FormSection>

          <FormSection :title="$t('global:form.thematicPages.showOnPortal')">
            <Radio
              v-model="formData.showOnVitrine"
              :items="yesNoOptions"
              :under-description="$t('global:form.thematicPages.showOnPortal.description')"
              horizontal
            />
          </FormSection>

          <FormSection :title="$t('global:form.thematicPages.creciSp')">
            <Radio
              v-model="formData.isCreciSp"
              :items="yesNoOptions"
              :under-description="$t('global:form.thematicPages.creciSp.description')"
              horizontal
            />
          </FormSection>

          <FormSection :title="$t('global:form.thematicPages.accesscode.title')">
            <Radio
              v-model="formData.restrictedAccess"
              :items="yesNoOptions"
              horizontal
            />

            <InputField
              v-if="formData.restrictedAccess"
              v-model="formData.restrictedAccessTitle"
              :under-description="$t('global:form.thematicPages.accesscode.restricted.access.title')"
              type="text"
              :min="1"
              :max="100"
              :counter="100"
              :label="$t('global:form.thematicPages.accesscode.restricted.access.title.text')"
              :validation="$v.formData.restrictedAccessTitle"
              :is-round-value-number="false"
            />

            <InputField
              v-if="formData.restrictedAccess"
              v-model="formData.restrictedAccessText"
              :under-description="$t('global:form.thematicPages.accesscode.restricted.access')"
              type="text"
              :min="1"
              :max="200"
              :counter="200"
              :label="$t('global:form.thematicPages.accesscode.restricted.access.text')"
              :validation="$v.formData.restrictedAccessText"
              :is-round-value-number="false"
            />

            <InputField
              v-if="formData.restrictedAccess"
              v-model="formData.accessCode"
              :under-description="$t('global:form.thematicPages.accesscode.description')"
              type="text"
              :min="1"
              :counter="10"
              :label="$t('global:form.thematicPages.accesscode.code')"
              :validation="$v.formData.accessCode"
              :is-round-value-number="false"
            />
          </FormSection>

          <FormSection
            :title="$t('global:form.thematicPages.privacyNotice.title')"
          >
            <Radio
              v-model="formData.privacyNotice"
              :items="yesNoOptions"
              horizontal
            />

            <FormSection
              v-if="formData.privacyNotice"
              :title="$t('global:form.thematicPages.privacyNotice.description')"
            >
              <RichTextEditor
                v-model="formData.privacyNoticeDescription"
                :validation="$v.formData.privacyNoticeDescription"
                :toolbar="RICH_TEXT_EDITOR_TOOLBAR"
                :max-length="500"
                class="rich-text"
              />
            </FormSection>
          </FormSection>
        </form>
      </div>
    </div>
    <div
      v-if="activeTab === 2"
      class="main-content-inner"
    >
      <div class="center">
        <div class="thematic-pages-create-header">
          <h2 class="thematic-pages-create-title">
            {{ $t("global:form.step", { num: 2 }) }}
          </h2>
          <p
            class="thematic-pages-create-description"
            v-html="$t('thematicPages.create:header.description.2')"
          />
          <span class="under-description">{{ $t("thematicPages.create:header.description.2.under-description") }}</span>
        </div>
        <form
          class="form"
          @submit.prevent="submit()"
        >
          <InputField
            v-model="formData.seoTitle"
            :label="$t('global:form.thematicPages.seo.title')"
          />
          <InputField
            v-model="formData.seoDescription"
            :label="$t('global:form.thematicPages.seo.description')"
          />
          <InputField
            v-model="formData.seoKeywords"
            :label="$t('global:form.thematicPages.seo.keywords')"
          />
        </form>
      </div>
    </div>
    <div
      v-if="activeTab === 3"
      class="main-content-inner"
    >
      <div class="center">
        <div class="thematic-pages-create-header">
          <h2 class="thematic-pages-create-title">
            {{ $t("global:form.step", { num: 3 }) }}
          </h2>
          <p
            class="thematic-pages-create-description"
            v-html="$t('thematicPages.create:header.description.3')"
          />
        </div>
        <form
          class="center"
          @submit.prevent="submit()"
        >
          <Tabs
            slot="tabs"
            :links="computedSelectionTabs"
            :index-active="selectionTabActive"
            @tabChange="selectionTabActive = $event"
          />

          <div
            v-show="selectionTabActive === 0"
            class="link-selection"
          >
            <FormSection :title="$t('show.in.Solutions')">
              <Radio
                v-model="showSolutions"
                :items="yesNoOptions"
                horizontal
                @input="clearLinkedSolutions($event)"
              />
            </FormSection>
            <div
              v-if="showSolutions"
              class="link-table"
            >
              <div class="linkable-items">
                <Datatable
                  :is-loading-more="isLoadingMoreSolutions"
                  :items="availableSolutions"
                  hide-horizontal-line
                  has-infinite-scroll
                  has-fade
                  empty-message="no_result"
                  @loadMore="loadMoreSolutions"
                >
                  <template slot="thead">
                    <th
                      class="th"
                      width="300"
                    >
                      {{ $t("solutions.list.link:datatable.header.1") }}
                    </th>
                  </template>
                  <template slot="tsearch">
                    <FilterListSearch
                      slot="search"
                      :prepend-label="$t('global:form.solution-search')"
                      :hide-error-details="true"
                      class="filter-search"
                      @search="searchSolution"
                    />
                  </template>
                  <template
                    slot="tbody"
                    slot-scope="props"
                  >
                    <tr
                      :key="'solution-option-' + props.item.id"
                      @click="addToLinkedSolution(props.item.id)"
                    >
                      <td class="td td-full">
                        <span class="td-text bolder">{{ props.item.name }}</span>
                        <br>
                        <span
                          class="td-text td-small"
                        >{{ getSolutionTypeFormattedName(props.item.solutionType) }} | {{ props.item.workload }}</span>
                        <br>
                        <span class="td-text td-small">
                          {{ $t("solutions.list:datatable.header.6") }}:
                          {{ Boolean(props.item.showOnPortal) ? $t("global:yes") : $t("global:no") }}
                        </span>
                      </td>
                      <td class="td td-button td-small text-right">
                        <Action
                          type="button"
                          icon="keyboard_arrow_right"
                          flat
                        />
                      </td>
                    </tr>
                  </template>
                </Datatable>
              </div>
              <Divisor orientation="vertical" />
              <div class="linked-items">
                <Datatable
                  has-fade
                  :items="formData.thematicPageSolutions"
                  hide-horizontal-line
                  :empty-message="$t('global:linked.items.empty')"
                >
                  <template slot="thead">
                    <th class="th">
                      {{ $t("solutions.list.link:datatable.header.2") }}
                    </th>
                  </template>
                  <template
                    slot="tbody"
                    slot-scope="props"
                  >
                    <tr :key="props.item.id">
                      <td class="td td-full">
                        <span class="td-text bolder">{{ props.item.name }}</span>
                      </td>
                      <td class="td td-button td-small text-right">
                        <Action
                          type="button"
                          icon="close"
                          flat
                          @click="removeLinkedSolution(props.item.id)"
                        />
                      </td>
                    </tr>
                  </template>
                </Datatable>
              </div>
            </div>
          </div>

          <div
            v-show="selectionTabActive === 1"
            class="link-selection"
          >
            <FormSection :title="$t('show.in.Paths')">
              <Radio
                v-model="showPaths"
                :items="yesNoOptions"
                horizontal
                @input="clearLinkedPaths($event)"
              />
            </FormSection>
            <div
              v-if="showPaths"
              class="link-table"
            >
              <div class="linkable-items">
                <Datatable
                  :is-loading-more="isLoadingMorePaths"
                  :items="availablePaths"
                  hide-horizontal-line
                  has-infinite-scroll
                  has-fade
                  empty-message="no_result"
                  @loadMore="loadMorePaths"
                >
                  <template slot="thead">
                    <th
                      class="th"
                      width="300"
                    >
                      {{ $t("paths.list:datatable.header.1") }}
                    </th>
                  </template>
                  <template slot="tsearch">
                    <FilterListSearch
                      slot="search"
                      :prepend-label="$t('global:form.track-search')"
                      :hide-error-details="true"
                      class="filter-search"
                      @search="searchPath"
                    />
                  </template>
                  <template
                    slot="tbody"
                    slot-scope="props"
                  >
                    <tr
                      :key="'path-option-' + props.item.id"
                      @click="addToLinkedPath(props.item.id)"
                    >
                      <td class="td td-full">
                        <span class="td-text bolder">{{ props.item.name }}</span>
                        <br>
                        <span class="td-text td-small">
                          {{ props.item.learningPathSolutions }}
                          {{ props.item.learningPathSolutions === 1 ? "Solução" : "Soluções" }} |
                          {{ props.item.workload ? props.item.workload : "0h" }}
                        </span>
                        <br>
                        <span class="td-text td-small">
                          {{ $t("solutions.list:datatable.header.6") }}:
                          {{ Boolean(props.item.showOnPortal) ? $t("global:yes") : $t("global:no") }}
                        </span>
                      </td>
                      <td class="td td-button td-small text-right">
                        <Action
                          type="button"
                          icon="keyboard_arrow_right"
                          flat
                        />
                      </td>
                    </tr>
                  </template>
                </Datatable>
              </div>
              <Divisor orientation="vertical" />
              <div class="linked-items">
                <Datatable
                  has-fade
                  :items="formData.thematicPageLearningPaths"
                  hide-horizontal-line
                  :empty-message="$t('global:linked.items.empty')"
                >
                  <template slot="thead">
                    <th class="th">
                      {{ $t("paths.list:datatable.header.2") }}
                    </th>
                  </template>
                  <template
                    slot="tbody"
                    slot-scope="props"
                  >
                    <tr :key="props.item.id">
                      <td class="td td-full">
                        <span class="td-text bolder">{{ props.item.name }}</span>
                      </td>
                      <td class="td td-button td-small text-right">
                        <Action
                          type="button"
                          icon="close"
                          flat
                          @click="removeLinkedPath(props.item.id)"
                        />
                      </td>
                    </tr>
                  </template>
                </Datatable>
              </div>
            </div>
          </div>

          <div
            v-show="selectionTabActive === 2"
            class="link-selection"
          >
            <FormSection :title="$t('show.in.Programs')">
              <Radio
                v-model="showPrograms"
                :items="yesNoOptions"
                horizontal
                @input="clearLinkedPrograms($event)"
              />
            </FormSection>
            <div
              v-if="showPrograms"
              class="link-table"
            >
              <div class="linkable-items">
                <Datatable
                  :is-loading-more="isLoadingMorePrograms"
                  :items="availablePrograms"
                  hide-horizontal-line
                  has-infinite-scroll
                  has-fade
                  empty-message="no_result"
                  @loadMore="loadMorePrograms"
                >
                  <template slot="thead">
                    <th
                      class="th"
                      width="300"
                    >
                      {{ $t("programs.list:datatable.path.header.1") }}
                    </th>
                  </template>
                  <template slot="tsearch">
                    <FilterListSearch
                      slot="search"
                      :prepend-label="$t('global:form.program-search')"
                      :hide-error-details="true"
                      class="filter-search"
                      @search="searchProgram"
                    />
                  </template>
                  <template
                    slot="tbody"
                    slot-scope="props"
                  >
                    <tr
                      :key="'program-option-' + props.item.id"
                      @click="addToLinkedProgram(props.item.id)"
                    >
                      <td class="td td-full">
                        <span class="td-text bolder">{{ props.item.name }}</span>
                        <br>
                        <span class="td-text td-small">
                          {{ props.item.programSolutions }}
                          {{ props.item.programSolutions === 1 ? "Solução" : "Soluções" }} |
                          {{ props.item.workload ? props.item.workload : "0h" }}
                        </span>
                        <br>
                        <span class="td-text td-small">
                          {{ $t("solutions.list:datatable.header.6") }}:
                          {{ Boolean(props.item.showOnPortal) ? $t("global:yes") : $t("global:no") }}
                        </span>
                      </td>
                      <td class="td td-button td-small text-right">
                        <Action
                          type="button"
                          icon="keyboard_arrow_right"
                          flat
                        />
                      </td>
                    </tr>
                  </template>
                </Datatable>
              </div>
              <Divisor orientation="vertical" />
              <div class="linked-items">
                <Datatable
                  has-fade
                  :items="formData.thematicPagePrograms"
                  hide-horizontal-line
                  :empty-message="$t('global:linked.items.empty')"
                >
                  <template slot="thead">
                    <th class="th">
                      {{ $t("programs.list:datatable.path.header.2") }}
                    </th>
                  </template>
                  <template
                    slot="tbody"
                    slot-scope="props"
                  >
                    <tr :key="props.item.id">
                      <td class="td td-full">
                        <span class="td-text bolder">{{ props.item.name }}</span>
                      </td>
                      <td class="td td-button td-small text-right">
                        <Action
                          type="button"
                          icon="close"
                          flat
                          @click="removeLinkedProgram(props.item.id)"
                        />
                      </td>
                    </tr>
                  </template>
                </Datatable>
              </div>
            </div>
          </div>

          <div
            v-show="selectionTabActive === 3"
            class="link-selection"
          >
            <FormSection :title="$t('show.in.Tools')">
              <Radio
                v-model="showTools"
                :items="yesNoOptions"
                horizontal
                @input="clearLinkedTools($event)"
              />
            </FormSection>
            <div
              v-if="showTools"
              class="link-table"
            >
              <div class="linkable-items">
                <Datatable
                  :is-loading-more="isLoadingMoreTools"
                  :items="availableTools"
                  hide-horizontal-line
                  has-infinite-scroll
                  has-fade
                  empty-message="no_result"
                  @loadMore="loadMoreTools"
                >
                  <template slot="thead">
                    <th
                      class="th"
                      width="300"
                    >
                      {{ $t("tools.list:datatable.header.1") }}
                    </th>
                  </template>
                  <template slot="tsearch">
                    <FilterListSearch
                      slot="search"
                      :prepend-label="$t('global:form.tool-search')"
                      :hide-error-details="true"
                      class="filter-search"
                      @search="searchTool"
                    />
                  </template>
                  <template
                    slot="tbody"
                    slot-scope="props"
                  >
                    <tr
                      :key="'tool-option-' + props.item.id"
                      @click="addToLinkedTool(props.item.id)"
                    >
                      <td class="td td-full">
                        <span class="td-text bolder">{{ props.item.title }}</span>
                        <br>
                        <span class="td-text td-small">
                          {{ $t("tools.list:datatable.header.2") }}:
                          {{ Boolean(props.item.active) ? $t("global:yes") : $t("global:no") }}
                        </span>
                      </td>
                      <td class="td td-button td-small text-right">
                        <Action
                          type="button"
                          icon="keyboard_arrow_right"
                          flat
                        />
                      </td>
                    </tr>
                  </template>
                </Datatable>
              </div>
              <Divisor orientation="vertical" />
              <div class="linked-items">
                <Datatable
                  has-fade
                  :items="formData.thematicPageTools"
                  hide-horizontal-line
                  :empty-message="$t('global:linked.items.empty')"
                >
                  <template slot="thead">
                    <th class="th">
                      {{ $t("tools.list:datatable.header.3") }}
                    </th>
                  </template>
                  <template
                    slot="tbody"
                    slot-scope="props"
                  >
                    <tr :key="props.item.id">
                      <td class="td td-full">
                        <span class="td-text bolder">{{ props.item.title }}</span>
                      </td>
                      <td class="td td-button td-small text-right">
                        <Action
                          type="button"
                          icon="close"
                          flat
                          @click="removeLinkedTool(props.item.id)"
                        />
                      </td>
                    </tr>
                  </template>
                </Datatable>
              </div>
            </div>
          </div>

          <div
            v-if="isCorporativePage"
            v-show="selectionTabActive === 4"
            class="link-selection"
          >
            <FormSection :title="$t('thematicPages.featured:link.featured.contents')">
              <Radio
                v-model="showFeaturedContents"
                :items="yesNoOptions"
                horizontal
                :disabled="!hasSelectedContents"
                @input="clearFeaturedContents()"
              />
            </FormSection>

            <div
              v-if="!hasSelectedContents"
              class="no-contents-message"
            >
              <p>{{ $t('thematicPages.featured:no.contents.selected') }}</p>
            </div>

            <div
              v-if="showFeaturedContents && hasSelectedContents"
              class="link-table featured-contents"
            >
              <div class="featured-info-wrapper">
                <p class="featured-info">
                  {{ $t('thematicPages.featured:description') }}
                  <span class="featured-counter">({{ formData.featuredContents.length }}/5)</span>
                </p>
              </div>
            </div>

            <div
              v-if="showFeaturedContents && hasSelectedContents"
              class="link-table featured-contents"
            >
              <div class="linkable-items">
                <Datatable
                  :items="availableFeaturedContents"
                  hide-horizontal-line
                  has-fade
                  empty-message="no_result"
                >
                  <template slot="thead">
                    <th class="th">
                      {{ $t('thematicPages.featured:available.contents.header') }}
                    </th>
                  </template>
                  <template
                    slot="tbody"
                    slot-scope="props"
                  >
                    <tr
                      :key="`available-${props.item.contentType}-${props.item.id}`"
                      :class="{ 'disabled-row': formData.featuredContents.length >= 5 }"
                      @click="addToFeaturedContents(props.item)"
                    >
                      <td class="td td-full">
                        <span class="td-text bolder">{{ props.item.name }}</span>
                        <br>
                        <span class="td-text td-small content-type-badge">
                          {{ getContentTypeLabel(props.item.contentType) }}
                        </span>
                      </td>
                      <td class="td td-button td-small text-right">
                        <Action
                          type="button"
                          icon="keyboard_arrow_right"
                          flat
                          :disabled="formData.featuredContents.length >= 5"
                        />
                      </td>
                    </tr>
                  </template>
                </Datatable>
              </div>
              <Divisor orientation="vertical" />
              <div class="linked-items">
                <Datatable
                  has-fade
                  :items="formData.featuredContents"
                  hide-horizontal-line
                  empty-message="thematicPages.featured:empty.message"
                >
                  <template slot="thead">
                    <th class="th">
                      {{ $t('thematicPages.featured:featured.contents.header') }}
                    </th>
                  </template>
                  <template
                    slot="tbody"
                    slot-scope="props"
                  >
                    <tr :key="`featured-${props.item.contentType}-${props.item.id}`">
                      <td class="td td-full">
                        <span class="td-text bolder">{{ props.item.name }}</span>
                        <br>
                        <span class="td-text td-small content-type-badge">
                          {{ getContentTypeLabel(props.item.contentType) }}
                        </span>
                      </td>
                      <td class="td td-button td-small text-right">
                        <Action
                          type="button"
                          icon="close"
                          flat
                          @click="removeFromFeaturedContents(props.item)"
                        />
                      </td>
                    </tr>
                  </template>
                </Datatable>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
    <div
      v-if="activeTab === 4"
      class="main-content-inner"
    >
      <div class="center">
        <div class="thematic-pages-create-header">
          <h2 class="thematic-pages-create-title">
            {{ $t("global:form.step", { num: 4 }) }}
          </h2>
          <p
            class="thematic-pages-create-description"
            v-html="$t('thematicPages.create:header.description.4')"
          />
        </div>

        <form
          class="center"
          @submit.prevent="submit()"
        >
          <Tabs
            slot="tabs"
            :links="computedOrderingTabs"
            :index-active="orderingTabActive"
            @tabChange="orderingTabActive = $event"
          />

          <div
            v-show="orderingTabActive === 0"
            class="link-selection"
          >
            <FormSection :title="$t('thematicPages.order')">
              <Radio
                key="radio-contents-ordering"
                v-model="sortingTypeContents"
                :items="computedDefaultManualOptions"
                horizontal
                :disabled="formData.thematicPageContents.length === 0"
              />
            </FormSection>

            <div
              v-if="formData.thematicPageContents.length > 0"
              class="link-table unified-contents"
            >
              <div class="linked-items full-width">
                <Datatable
                  :drag="sortingTypeContents === 'manual'"
                  :items="formData.thematicPageContents"
                  hide-horizontal-line
                  empty-message="thematicPages.contents:empty.message"
                >
                  <template slot="thead">
                    <th class="th">
                      {{ $t('thematicPages.contents:unified.header') }}
                    </th>
                  </template>
                  <template
                    slot="tbody"
                    slot-scope="props"
                  >
                    <tr
                      :key="`unified-${props.item.solutionType}-${props.item.id}`"
                      class="tr-draggable"
                    >
                      <td class="td td-drag">
                        <Icon
                          v-show="sortingTypeContents === 'manual'"
                          name="drag"
                          size="is-small"
                        />
                      </td>
                      <td class="td td-full">
                        <span class="td-text bolder">{{ props.item.name }}</span>
                        <br>
                        <span class="td-text td-small content-type-badge">
                          {{ getContentTypeLabel(props.item.solutionType) }}
                        </span>
                      </td>
                      <td class="td td-button td-small text-right">
                        <div class="ordering-actions">
                          <Action
                            v-show="sortingTypeContents === 'manual'"
                            class="order-action"
                            type="button"
                            icon="arrow_drop_down"
                            flat
                            icon-size="large"
                            @click="moveUnifiedContentDown(props.item)"
                          />
                          <Action
                            v-show="sortingTypeContents === 'manual'"
                            class="order-action"
                            type="button"
                            icon="arrow_drop_up"
                            flat
                            icon-size="large"
                            @click="moveUnifiedContentUp(props.item)"
                          />
                        </div>
                      </td>
                    </tr>
                  </template>
                </Datatable>
              </div>
            </div>

            <div
              v-else
              class="no-contents-message"
            >
              <p>{{ $t('thematicPages.contents:no.contents') }}</p>
            </div>
          </div>

          <div
            v-if="isCorporativePage"
            v-show="orderingTabActive === 1"
            class="link-selection"
          >
            <FormSection :title="$t('thematicPages.order')">
              <Radio
                key="radio-featured-ordering"
                v-model="sortingTypeFeatured"
                :items="computedDefaultManualOptions"
                horizontal
                :disabled="!showFeaturedContents || formData.featuredContents.length === 0"
              />
            </FormSection>

            <div
              v-if="showFeaturedContents && formData.featuredContents.length > 0"
              class="link-table featured-contents"
            >
              <div class="linked-items full-width">
                <Datatable
                  :drag="sortingTypeFeatured === 'manual'"
                  :items="formData.featuredContents"
                  hide-horizontal-line
                  empty-message="thematicPages.featured:empty.message"
                >
                  <template slot="thead">
                    <th class="th">
                      {{ $t('thematicPages.featured:featured.contents.header') }}
                    </th>
                  </template>
                  <template
                    slot="tbody"
                    slot-scope="props"
                  >
                    <tr
                      :key="`featured-order-${props.item.contentType}-${props.item.id}`"
                      class="tr-draggable"
                    >
                      <td class="td td-drag">
                        <Icon
                          v-show="sortingTypeFeatured === 'manual'"
                          name="drag"
                          size="is-small"
                        />
                      </td>
                      <td class="td td-full">
                        <span class="td-text bolder">{{ props.item.name }}</span>
                        <br>
                        <span class="td-text td-small content-type-badge">
                          {{ getContentTypeLabel(props.item.contentType) }}
                        </span>
                      </td>
                      <td class="td td-button td-small text-right">
                        <div class="ordering-actions">
                          <Action
                            v-show="sortingTypeFeatured === 'manual'"
                            class="order-action"
                            type="button"
                            icon="arrow_drop_down"
                            flat
                            icon-size="large"
                            @click="moveFeaturedContentDown(props.item)"
                          />
                          <Action
                            v-show="sortingTypeFeatured === 'manual'"
                            class="order-action"
                            type="button"
                            icon="arrow_drop_up"
                            flat
                            icon-size="large"
                            @click="moveFeaturedContentUp(props.item)"
                          />
                        </div>
                      </td>
                    </tr>
                  </template>
                </Datatable>
              </div>
            </div>

            <div
              v-else
              class="no-contents-message"
            >
              <p>{{ $t('thematicPages.featured:no.featured.contents') }}</p>
            </div>
          </div>
        </form>
      </div>
    </div>
    <Pagination
      class="center"
      :active-first-last="false"
      :active-page="activeTab"
      :page-count="maxStepsCount"
      @nextPage="nextPage()"
      @previousPage="prevPage()"
    />
    <ModalConfirm
      :active="modalActiveThematicPageOpen"
      :title="$t('thematicPages.list:datatable.header.1')"
      :description="$t('thematicPages:feedback.confirm.activate')"
      @confirmAction="saveAllConfirm"
      @cancelAction="saveAllCancel"
    />
    <ModalConfirm
      :active="modalLeaveOpen"
      :title="$t('global:form.leave.title')"
      :description="$t('global:form.leave.description')"
      @confirmAction="confirmWizardLeave"
      @cancelAction="cancelWizardLeave"
    />
  </div>
</template>

<style>
.thematic-pages-create .form {
  max-width: 480px;
  margin: 0 auto;
}
.link-selection,
.link-selection .form-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 0;
  padding-bottom: 20px;
}

.link-selection .link-table > div {
  width: 50%;
}

.link-table {
  display: flex;
  flex-direction: row;
}

.link-table.featured-contents .th{
  width: 100%;
}

tr.tr-draggable td .ordering-actions {
  flex-direction: column-reverse;
  display: flex;
}

tr.tr-draggable td .order-action {
  width: 36px;
  padding: 0;
}

.drag-container tr.tr-draggable td .order-action .icon-wrapper {
  height: 36px;
  width: 36px;
}

.thematic-pages-create-header + .center .tabs {
  display: flex;
  justify-content: center;
  margin: 65px 0 80px;
}

.under-description {
  display: inline-block;
  font-size: 1.2em;
  color: rgba(0, 0, 0, 0.5);
  padding: 5px 0;
  max-width: 480px;
  text-transform: none;
}

.hex-under-description {
  margin-bottom: 10px;
}

.featured-info {
  text-align: center;
  font-size: 1.4em;
  color: #666;
  margin-bottom: 20px;
}

.featured-counter {
  font-weight: bold;
  color: #005eb8;
}

.featured-info-wrapper {
  width: 100%;
  margin-bottom: 20px;
}

.content-type-badge {
  color: #666;
}

.disabled-row {
  opacity: 0.5;
  cursor: not-allowed !important;
}

.disabled-row:hover {
  background-color: transparent !important;
}

.linked-items.full-width {
  width: 100% !important;
  min-width: 100% !important;
  max-width: 800px;
  margin: 0 auto;
}

.unified-contents .linked-items.full-width,
.featured-contents .linked-items.full-width {
  width: 100% !important;
}

.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}

.drag-container {
  min-height: 50px;
}

.no-contents-message {
  text-align: center;
  padding: 40px;
  color: #666;
  font-size: 1.2em;
}

.no-contents-message p {
  margin: 0;
}

.unified-contents .td.td-full,
.featured-contents .td.td-full {
  height: 120px;
}
</style>
